"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { markHadPro, browserStorage } from "./returning-pro";
import posthog from "posthog-js";

interface SubscriptionData {
  authenticated: boolean;
  email?: string;
  hasPro: boolean;
  subscription: {
    status: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
  } | null;
}

export function useSubscription() {
  const [data, setData] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasIdentified = useRef(false);

  const checkSubscription = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch("/api/subscription");
      const result = await response.json();

      if (result.error) {
        setError(result.error);
      } else {
        setData(result);
        setError(null);
        // So a later signed-out visit can offer "sign in to restore" before "upgrade"
        if (result.hasPro) markHadPro(browserStorage());

        // Identify user in PostHog when authenticated
        if (result.authenticated && result.email && !hasIdentified.current) {
          posthog.identify(result.email, {
            email: result.email,
            has_pro: result.hasPro,
            subscription_status: result.subscription?.status,
          });
          hasIdentified.current = true;
        }
      }
    } catch (err) {
      setError("Failed to check subscription");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  const startCheckout = async (priceId?: string, couponId?: string) => {
    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          priceId,
          email: data?.email,
          couponId,
        }),
      });

      const result = await response.json();

      if (result.url) {
        window.location.href = result.url;
      } else {
        throw new Error(result.error || "Failed to start checkout");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Checkout failed");
    }
  };

  const openPortal = async () => {
    try {
      const response = await fetch("/api/portal", {
        method: "POST",
      });

      const result = await response.json();

      if (result.url) {
        window.location.href = result.url;
      } else {
        throw new Error(result.error || "Failed to open portal");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Portal access failed");
    }
  };

  const logout = async () => {
    try {
      await fetch("/api/logout", { method: "POST" });
      // Reload page to reset state
      window.location.reload();
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  const restoreAccess = async (email: string) => {
    try {
      const response = await fetch("/api/restore", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (result.status === "email_sent") {
        // Magic link sent - user needs to check email
        return { success: true, emailSent: true, message: result.message };
      } else if (result.status === "expired") {
        return {
          success: false,
          expired: true,
          message: result.message,
          portalUrl: result.portalUrl,
        };
      } else {
        return { success: false, message: result.error || "Failed to restore" };
      }
    } catch (err) {
      return { success: false, message: "Failed to restore access" };
    }
  };

  return {
    authenticated: data?.authenticated ?? false,
    email: data?.email,
    hasPro: data?.hasPro ?? false,
    subscription: data?.subscription,
    loading,
    error,
    startCheckout,
    openPortal,
    logout,
    restoreAccess,
    refresh: checkSubscription,
  };
}
